import Layout from '@components/layout'
import { blogPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogPage } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getBlogCanonicalUrl } from '@lib/blog'
import { Locale } from '@lib/language'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import { getBlogLocale, getBlogPage } from '@lib/sanity/blog'
import { defaultModuleQueryParameters } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopPageProps = BasePageProps & {
  blogLocale: Locale
  page: SanityBlogPage
  site: SanitySiteFragment
}

const BlogPage = ({
  draftMode,
  locale,
  blogLocale,
  page,
  site,
}: ShopPageProps) => {
  const canonicalUrl = getBlogCanonicalUrl('blogPage', '', site.blogSettings)

  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={blogPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<ShopPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const blogPage = await getBlogPage(sanityClient, context.locale, blogLocale)

  if (!blogPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      page: getNonNullValues(blogPage.page),
      site: getNonNullValues(blogPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default BlogPage
